import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Check,
  CheckWithLines,
  ListChecksQuery,
  WriteCheckInput,
} from '@accounting/shared';
import { api } from '../lib/api';

export const checksKey = (companyId: string | null, filters: Partial<ListChecksQuery> = {}) =>
  ['checks', companyId ?? 'none', filters.bankAccountId ?? 'all', filters.status ?? 'any'] as const;

export function useChecks(companyId: string | null, filters: Partial<ListChecksQuery> = {}) {
  return useQuery({
    queryKey: checksKey(companyId, filters),
    enabled: companyId !== null,
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters.bankAccountId) params.set('bankAccountId', filters.bankAccountId);
      if (filters.status) params.set('status', filters.status);
      const qs = params.toString();
      return api
        .get<{ checks: Check[] }>(`/companies/${companyId}/checks${qs ? '?' + qs : ''}`)
        .then((r) => r.checks);
    },
  });
}

export function useCheck(companyId: string | null, id: string) {
  return useQuery({
    queryKey: ['check', companyId ?? 'none', id],
    enabled: companyId !== null && !!id,
    queryFn: () =>
      api.get<{ check: CheckWithLines }>(`/companies/${companyId}/checks/${id}`).then((r) => r.check),
  });
}

export function useNextCheckNumber(companyId: string | null, bankAccountId: string) {
  return useQuery({
    queryKey: ['check-next-number', companyId ?? 'none', bankAccountId],
    enabled: companyId !== null && !!bankAccountId,
    queryFn: () =>
      api
        .get<{ checkNumber: string }>(
          `/companies/${companyId}/checks/next-number?bankAccountId=${bankAccountId}`,
        )
        .then((r) => r.checkNumber),
  });
}

function invalidate(qc: ReturnType<typeof useQueryClient>, companyId: string | null) {
  qc.invalidateQueries({ queryKey: ['checks', companyId ?? 'none'] });
  qc.invalidateQueries({ queryKey: ['check', companyId ?? 'none'] });
  qc.invalidateQueries({ queryKey: ['check-next-number', companyId ?? 'none'] });
  qc.invalidateQueries({ queryKey: ['journal-entries', companyId ?? 'none'] });
  qc.invalidateQueries({ queryKey: ['accounts-balances', companyId ?? 'none'] });
  // A check can pay an accrued paycheque; the uncashed list changes with it.
  qc.invalidateQueries({ queryKey: ['payroll-uncashed', companyId ?? 'none'] });
  qc.invalidateQueries({ queryKey: ['report'] });
}

export function useWriteCheck(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: WriteCheckInput) => {
      if (!companyId) throw new Error('No active company');
      return api
        .post<{ check: CheckWithLines }>(`/companies/${companyId}/checks`, input)
        .then((r) => r.check);
    },
    onSuccess: () => invalidate(qc, companyId),
  });
}

export function useUpdateCheck(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: WriteCheckInput }) => {
      if (!companyId) throw new Error('No active company');
      return api
        .patch<{ check: CheckWithLines }>(`/companies/${companyId}/checks/${id}`, input)
        .then((r) => r.check);
    },
    onSuccess: () => invalidate(qc, companyId),
  });
}

export function useVoidCheck(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => {
      if (!companyId) throw new Error('No active company');
      return api.post<{ check: Check }>(`/companies/${companyId}/checks/${id}/void`, { reason });
    },
    onSuccess: () => invalidate(qc, companyId),
  });
}

export function useSetCheckCleared(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, cleared }: { id: string; cleared: boolean }) => {
      if (!companyId) throw new Error('No active company');
      return api.post<{ check: Check }>(`/companies/${companyId}/checks/${id}/clear`, { cleared });
    },
    onSuccess: () => invalidate(qc, companyId),
  });
}
